import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adapter } from '../providers/adapter';

// Debe coincidir con el "Email OTP Expiration" configurado en el proyecto
// de Supabase (Authentication → Providers → Email).
const CODE_TTL_SECONDS = 3600;

export function useVerifyRecoveryCode(email: string) {
  const { t } = useTranslation('auth');
  const [isPending, setIsPending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState<Error | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(CODE_TTL_SECONDS);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const isExpired = secondsLeft <= 0;

  const verifyCode = async (code: string): Promise<void> => {
    setIsPending(true);
    setError(null);
    try {
      await adapter.verifyRecoveryOtp(email, code);
      setIsSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(t('errors.generic')));
    }
    setIsPending(false);
  };

  const resendCode = async (): Promise<void> => {
    setIsResending(true);
    setResendError(null);
    try {
      await adapter.resetPasswordForEmail(email);
      setSecondsLeft(CODE_TTL_SECONDS);
    } catch (err) {
      setResendError(err instanceof Error ? err : new Error(t('errors.generic')));
    }
    setIsResending(false);
  };

  return {
    verifyCode,
    isPending,
    isSuccess,
    error,
    resendCode,
    isResending,
    resendError,
    secondsLeft,
    isExpired,
  };
}
